import type { ExerciseType, Phase } from "./types";

export function phaseForWeek(phases: Phase[], week: number): Phase | null {
  return (
    phases.find((p) => week >= p.fromWeek && (p.toWeek == null || week <= p.toWeek)) ?? null
  );
}

/** Number of working sets for an exercise at `position` (0-based) in the given phase. */
export function effectiveSets(planned: number, position: number, phase: Phase | null): number {
  if (!phase) return planned;
  if (phase.setsMode === "fixed" && phase.fixedSets) return Math.min(planned, phase.fixedSets);
  if (phase.setsMode === "bonus" && position < (phase.bonusFirstN ?? 0)) {
    return planned + (phase.bonusSets ?? 0);
  }
  return planned;
}

/**
 * Next strength workout in the rotation: the one after the last finished one.
 * With two workouts this yields A B A / B A B across weeks, like the plan.
 */
export function nextInRotation<T extends { id: string }>(
  workouts: T[],
  lastWorkoutId: string | null | undefined,
): T | null {
  if (workouts.length === 0) return null;
  const idx = workouts.findIndex((w) => w.id === lastWorkoutId);
  return workouts[(idx + 1) % workouts.length] ?? workouts[0];
}

export type DurationItem = {
  type: ExerciseType;
  /** Working sets (already adjusted for the phase). */
  sets: number;
  /** Top of the target range: reps, seconds or minutes depending on the type. */
  targetMax: number;
  restSec: number;
  warmupSets: number;
};

/** ~2 s down, ~2 s up per rep. */
const SEC_PER_REP = 4;
/** Walking to the next machine and setting it up. */
const SETUP_SEC = 60;
/** A warm-up set (~10 light reps) plus the 45 s rest the trainer gives after it. */
const WARMUP_SET_SEC = 30 + 45;
/** The general warm-up (cardio + mobility) before a strength workout. */
const GENERAL_WARMUP_SEC = 8 * 60;

/**
 * Rough duration of a workout in minutes (rounded to 5), from its sets, target
 * ranges and rest times. The trainer rests after every set except the very last.
 */
export function estimateWorkoutMinutes(items: DurationItem[], opts: { warmup?: boolean } = {}): number {
  let sec = 0;
  let lastRest = 0;
  for (const it of items) {
    if (it.type === "cardio") {
      sec += it.sets * it.targetMax * 60;
      continue;
    }
    const perSet = it.type === "time" ? it.targetMax : it.targetMax * SEC_PER_REP;
    const warmups = it.type === "reps" ? it.warmupSets : 0;
    sec += SETUP_SEC + warmups * WARMUP_SET_SEC + it.sets * (perSet + it.restSec);
    if (it.sets > 0) lastRest = it.restSec;
  }
  sec -= lastRest; // no rest after the last set
  if (opts.warmup && items.some((it) => it.type !== "cardio")) sec += GENERAL_WARMUP_SEC;
  if (sec <= 0) return 0;
  return Math.max(5, Math.round(sec / 300) * 5);
}
